import type { ChainAddress, WalletGroupedAccounts } from '@polaris/core/models'
import { Check, ScanQrCode } from 'lucide-react'
import Image from 'next/image'
import { lazy, Suspense, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/src/components/ui/dialog'
import {
  ListRowButton,
  ListRowSide,
  ListRowSideName,
} from '@/src/components/ui/list-row'
import { useChainColor } from '@/src/hooks/use-chain-color'
import { useAllDerivedAddresses } from '@/src/stores/accounts/addresses'
import theme from '@/tailwind.config'
import { Button } from '../ui/button'
import { ChainImg } from '../ui/chain-img'
import { NoSearchResults } from '../ui/no-search-results'
import { SearchField } from '../ui/search-field'
import { Skeleton } from '../ui/skeleton'
import { Spinner } from '../ui/spinner'

// Dynamically import QRCode component
const QRCode = lazy(() =>
  import('../ui/qrcode').then((module) => ({ default: module.QRCode })),
)

interface AddressDisplayViewProps {
  selectedNetwork: ChainAddress
}

function AddressDisplayView({ selectedNetwork }: AddressDisplayViewProps) {
  const { chainColor } = useChainColor(
    selectedNetwork.chainId,
    selectedNetwork.chainType,
  )

  const [copied, setCopied] = useState(false)

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(selectedNetwork.address)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy address:', err)
    }
  }

  return (
    <div className='flex flex-col justify-center items-center gap-6'>
      {/* QR Code Section */}
      <div
        style={{
          backgroundColor: chainColor
            ? `${chainColor}80`
            : theme.theme.colors.background,
        }}
        className='px-6 pt-6 rounded-2xl'
      >
        <Suspense
          fallback={
            <div className='w-[200px] h-[200px] flex items-center justify-center rounded-2xl animate-pulse'>
              <Spinner className='size-12 text-primary border-3' />
            </div>
          }
        >
          <QRCode
            value={selectedNetwork.address}
            size={240}
            logoSize={48}
            color={chainColor || theme.theme.colors.background}
            centerElement={
              <Image
                src={selectedNetwork.chainImg ?? ''}
                alt={`${selectedNetwork.chainName} logo`}
                width={48}
                height={48}
                className='rounded-full'
              />
            }
          />
        </Suspense>

        <div className='flex items-center justify-center gap-2 text-secondary py-3'>
          <ScanQrCode size={24} />
          <span className='text-sm'>Scan with a mobile device</span>
        </div>
      </div>

      {/* Address Info */}
      <div className='px-8 pb-6'>
        <p className='text-center text-secondary text-sm mb-6'>
          Use this address to receive tokens on{' '}
          <span className='text-primary font-medium'>
            {selectedNetwork.chainName}
          </span>{' '}
          network.
        </p>

        {/* Address Display */}
        <div className='p-4 mb-6 rounded-2xl border border-border1'>
          <label className='block text-secondary text-sm mb-2'>
            Your {selectedNetwork.chainName} address
          </label>
          <p className='text-primary leading-tight tracking-tight break-all font-mono'>
            {selectedNetwork.address}
          </p>
        </div>

        {/* Copy Button */}
        <Button
          onClick={copyAddress}
          className='w-full'
          variant='primary'
          size='md'
        >
          {copied ? 'Copied to clipboard' : 'Copy address'}
          {copied && <Check className='ml-1' />}
        </Button>
      </div>
    </div>
  )
}

interface AddressesDialogProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  wallet: WalletGroupedAccounts
}

/** Dialog that displays all addresses for a specific wallet. */
export function AddressesDialog({
  isOpen,
  onOpenChange,
  wallet,
}: AddressesDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogDescription className='hidden'>
        Select network to receive funds
      </DialogDescription>
      <Content wallet={wallet} onOpenChange={onOpenChange} />
    </Dialog>
  )
}

const Content = ({
  wallet,
  onOpenChange,
}: {
  wallet: WalletGroupedAccounts
  onOpenChange: (open: boolean) => void
}) => {
  const [query, setQuery] = useState('')
  const [selectedNetwork, setSelectedNetwork] = useState<ChainAddress | null>(
    null,
  )
  const { data: derivedAddresses = [], isLoading } = useAllDerivedAddresses(
    wallet.grouping_key,
  )

  // Filter addresses based on search query
  const filteredAddresses = useMemo(() => {
    return derivedAddresses.filter(
      (addr) =>
        addr.address.toLowerCase().includes(query.toLowerCase()) ||
        addr.chainName.toLowerCase().includes(query.toLowerCase()),
    )
  }, [derivedAddresses, query])

  const handleBack = () => {
    setSelectedNetwork(null)
    setQuery('')
  }

  const handleClose = () => {
    setSelectedNetwork(null)
    setQuery('')
    onOpenChange(false)
  }

  if (selectedNetwork) {
    return (
      <DialogContent
        aria-describedby={undefined}
        className='sm:max-w-[28rem] h-auto min-h-[unset] sm:min-w-[unset]'
        showCloseButton
        showBackButton
        closeAction={handleClose}
        backAction={handleBack}
        // middle={
        //   <DialogTitle className='text-xl font-semibold'>Add funds</DialogTitle>
        // }
      >
        <AddressDisplayView selectedNetwork={selectedNetwork} />
      </DialogContent>
    )
  }

  return (
    <DialogContent
      aria-describedby={undefined}
      showCloseButton
      closeAction={handleClose}
      className='max-w-[28rem]'
      middle={
        <DialogTitle className='text-xl font-semibold'>
          Select network
        </DialogTitle>
      }
    >
      <DialogDescription className='px-8 pb-4'>
        Select the network you will be using to receive funds.
      </DialogDescription>

      {/* Search */}
      <div className='flex w-full items-center justify-center px-8 pt-3 shadow-[0_1rem_1rem_var(--foreground)] z-10'>
        <SearchField
          dimension='medium'
          className='w-full'
          inputClassName='w-full placeholder:font-normal'
          data-testid='search-addresses'
          inputProps={{
            placeholder: 'Search addresses',
            value: query,
            onChange: (e) => setQuery(e.target.value),
            autoFocus: true,
          }}
          resetState={() => setQuery('')}
        />
      </div>

      {/* Network List */}
      <div className='flex flex-col w-full px-4 py-6 overflow-scroll no-scrollbar'>
        {isLoading ? (
          <div className='space-y-2'>
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className='h-16 w-full' />
            ))}
          </div>
        ) : filteredAddresses.length > 0 ? (
          filteredAddresses.map((addressData, index) => (
            <ListRowButton
              key={`${addressData.chainId}-${index}`}
              data-testid={`address-${addressData.chainId}-${index}`}
              onClick={() => setSelectedNetwork(addressData)}
              className='py-2'
            >
              <ListRowSide className='flex-row items-center gap-3'>
                <ChainImg
                  chainImg={addressData.chainImg}
                  chainName={addressData.chainName}
                  chainId={addressData.chainId}
                  width={20}
                  height={20}
                  containerClassName='size-8'
                />
                <ListRowSideName>{addressData.chainName}</ListRowSideName>
              </ListRowSide>
              <ListRowSide showIndicator />
            </ListRowButton>
          ))
        ) : (
          <NoSearchResults failedQuery={query} />
        )}
      </div>
    </DialogContent>
  )
}
